import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

import { JOB_TYPE_LABELS } from '../constants/jobTypes';

const CATEGORY_LABELS = {
  'legal': '法律合规',
  'finance': '财务审计',
  'business': '商务拓展',
  'engineering': '工程技术',
  'hr': '人力资源',
  'supply-chain': '供应链',
  'other': '其他',
};

const CATEGORY_ICONS = {
  'legal': '⚖️', 'finance': '💰', 'business': '🌐',
  'engineering': '🏗️', 'hr': '📋', 'supply-chain': '📦', 'other': '📄',
};

function Jobs() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [jobType, setJobType] = useState(searchParams.get('jobType') || '');
  const [page, setPage] = useState(1);
  const [favIds, setFavIds] = useState(new Set());

  useEffect(() => {
    if (!user) { setFavIds(new Set()); return; }
    api.get('/jobs/my/favorites')
      .then(res => setFavIds(new Set((res.data || []).map(j => j.id))))
      .catch(() => {});
  }, [user]);

  const toggleFav = async (e, jobId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await api.post(`/jobs/${jobId}/favorite`);
      setFavIds(prev => {
        const next = new Set(prev);
        if (res.data.favorited) next.add(jobId); else next.delete(jobId);
        return next;
      });
    } catch {}
  };

  useEffect(() => {
    fetchJobs();
  }, [category, jobType, page]);

  useEffect(() => {
    const s = searchParams.get('search');
    if (s) { setSearch(s); fetchJobs(s); }
  }, [searchParams]);

  const fetchJobs = async (searchOverride) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchOverride || search) params.set('search', searchOverride || search);
      if (category) params.set('category', category);
      if (jobType) params.set('jobType', jobType);
      params.set('page', page);

      const res = await api.get(`/jobs?${params.toString()}`);
      setJobs(res.data.jobs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchJobs();
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return '今天';
    if (days === 1) return '昨天';
    if (days < 30) return `${days}天前`;
    return `${Math.floor(days / 30)}个月前`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  };

  return (
    <div className="container" style={{ paddingTop: 20, paddingBottom: 60 }}>
      <div className="page-header">
        <h1 className="page-title">涉外岗位</h1>
        <p className="page-subtitle">共 {total} 个在招岗位</p>
      </div>

      {/* Search & Filters */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <input
          className="form-input"
          placeholder="搜索岗位、公司、关键词..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <button type="submit" className="btn btn-primary">搜索</button>
      </form>

      <div className="filter-bar">
        <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>
          <option value="">全部类别</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select value={jobType} onChange={e => { setJobType(e.target.value); setPage(1); }}>
          <option value="">全部类型</option>
          {Object.entries(JOB_TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Job List */}
      {loading ? (
        <div className="loading">加载中...</div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <p className="empty-state-text">暂无符合条件的岗位</p>
          <Link to="/post-job" className="btn btn-primary">发布第一个岗位</Link>
        </div>
      ) : (
        <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {jobs.map(job => (
            <Link key={job.id} to={`/jobs/${job.id}`} style={{ textDecoration: 'none' }}>
              <div className="card card-clickable job-card">
                <div className="job-card-icon">
                  {CATEGORY_ICONS[job.category] || '📄'}
                </div>
                <div className="job-card-content">
                  <div className="job-card-title">{job.title}</div>
                  <div className="job-card-company">{job.company}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-light)', margin: '4px 0' }}>
                    📍 {job.location}
                    {job.publishDate && <span style={{ marginLeft: 12 }}>📅 {job.publishDate}</span>}
                  </div>
                  <div className="job-card-meta">
                    <span className="tag">{JOB_TYPE_LABELS[job.jobType] || job.jobType}</span>
                    <span className="tag tag-purple">{CATEGORY_LABELS[job.category] || job.category}</span>
                  </div>
                  <div className="job-card-footer">
                    <span>{job.salary || '薪资面议'}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {job.viewCount || 0} 次查看 · {job.commentCount || 0} 条评论
                      {user && (
                        <button
                          onClick={(e) => toggleFav(e, job.id)}
                          title={favIds.has(job.id) ? '取消收藏' : '收藏'}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: '1.05rem', padding: 0,
                            color: favIds.has(job.id) ? 'var(--error, #e53e3e)' : 'var(--text-light)',
                          }}
                        >
                          {favIds.has(job.id) ? '★' : '☆'}
                        </button>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Pagination */}
        {total > 20 && (
          <div className="pagination">
            <button
              className="btn btn-secondary btn-sm"
              disabled={page <= 1}
              onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
            >上一页</button>
            <span className="pagination-info">
              第 {page} / {Math.max(1, Math.ceil(total / 20))} 页
            </span>
            <button
              className="btn btn-secondary btn-sm"
              disabled={page >= Math.ceil(total / 20)}
              onClick={() => { setPage(p => p + 1); window.scrollTo(0, 0); }}
            >下一页</button>
          </div>
        )}
        </>
      )}
    </div>
  );
}

export default Jobs;
